import React, { useMemo, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PeopleTableSkeleton from "../../../../components/dashboardcomponents/People/PeopleTableSkeleton";
import PeopleTable from "../../../../components/dashboardcomponents/People/PeopleTable";
import StatusChip from "../../../../components/dashboardcomponents/People/StatusChip";
import TeamToolbar from "../../../../components/dashboardcomponents/People/Teams/TeamToolbar";
import TeamBulkBar from "../../../../components/dashboardcomponents/People/Teams/TeamBulkBar";
import TeamNameCell from "../../../../components/dashboardcomponents/People/Teams/TeamNameCell";
import TeamArchiveModal from "../../../../components/dashboardcomponents/People/Teams/TeamArchiveModal";
import TeamDeleteModal from "../../../../components/dashboardcomponents/People/Teams/TeamDeleteModal";
import TeamActivateModal from "../../../../components/dashboardcomponents/People/Teams/TeamActivateModal";
import TeamCreateModal from "../../../../components/dashboardcomponents/People/Teams/TeamCreateModal";
import TeamDetailSidebar from "../../../../components/dashboardcomponents/People/Teams/TeamDetailSidebar";
import { TEAM_OPEN_CREATE_MODAL } from "../../../../components/dashboardcomponents/People/Teams/teamEvents";
import { parseListParam } from "../../../../components/dashboardcomponents/People/Customers/customersConfig";
import { filterTeams, mergePeopleSearchParams } from "../../../../utils/peopleHelpers";
import {
  buildTeamPayloadFromForm,
  buildTeamCreatePayloadFromForm,
  mapApiMemberToUi,
} from "../../../../utils/teamMappers";
import useTeamFormOptions from "../../../../hooks/useTeamFormOptions";
import usePeoplePermissions from "../../../../hooks/usePeoplePermissions";
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId";
import {
  fetchTeamsList,
  fetchTeamById,
  createTeam,
  updateTeam,
  archiveTeam,
  activateTeam,
  deleteTeam,
  bulkTeams,
} from "../../../../store/features/teamsApi";
import "../../TicketManagment/style.css";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function Teams() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedGroupId } = useAnalyticsGroupId();
  const tab = searchParams.get("tab") || "all";
  const search = searchParams.get("q") || "";
  const groupParam = searchParams.get("group") || "";

  const { list: rows, listLoading, detail, detailLoading, mutationLoading, lastFetchedListKey, cacheByListKey } = useSelector(
    (state) => state.peopleTeams
  );
  const { groupOptions, agentOptions, loading: formOptionsLoading } = useTeamFormOptions();
  const perms = usePeoplePermissions();

  const [groupFilters, setGroupFilters] = useState(() => parseListParam(groupParam));
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [bulkArchiveCount, setBulkArchiveCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteCount, setBulkDeleteCount] = useState(0);
  const [activateTarget, setActivateTarget] = useState(null);
  const [detailTeamId, setDetailTeamId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const rowsPerPage = 10;

  const patchParams = (updates) => mergePeopleSearchParams(setSearchParams, updates);

  const listQueryKey = selectedGroupId ? String(selectedGroupId) : "";
  const sourceRows = (listQueryKey && cacheByListKey?.[listQueryKey]) || rows;
  const isListStale =
    Boolean(selectedGroupId) &&
    !cacheByListKey?.[listQueryKey] &&
    listQueryKey !== lastFetchedListKey;
  const showListLoading = !selectedGroupId || listLoading || isListStale;

  const reloadList = useCallback(() => {
    if (!selectedGroupId) return;
    dispatch(fetchTeamsList({ group_id: selectedGroupId }));
  }, [dispatch, selectedGroupId]);

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  useEffect(() => {
    setGroupFilters(parseListParam(groupParam));
  }, [groupParam]);

  useEffect(() => {
    setSelected([]);
    setPage(0);
  }, [tab, search, groupParam, selectedGroupId]);

  const syncGroupFilters = (next) => {
    setGroupFilters(next);
    patchParams({ group: next.length ? next.join(",") : undefined });
  };

  useEffect(() => {
    const openCreate = () => setCreateModalOpen(true);
    window.addEventListener(TEAM_OPEN_CREATE_MODAL, openCreate);
    return () => window.removeEventListener(TEAM_OPEN_CREATE_MODAL, openCreate);
  }, []);

  useEffect(() => {
    if (!detailTeamId) return;
    dispatch(fetchTeamById({ teamId: detailTeamId }));
  }, [detailTeamId, dispatch]);

  const filteredRows = useMemo(
    () => filterTeams(sourceRows, { tab, search, groupFilters }),
    [sourceRows, tab, search, groupFilters]
  );

  const sidebarRawTeam = useMemo(() => {
    if (!detailTeamId) return null;
    if (detail?.id === detailTeamId) return detail;
    const row = sourceRows.find((r) => r.id === detailTeamId);
    return row?.raw || null;
  }, [detailTeamId, detail, sourceRows]);

  const columns = [
    {
      key: "name",
      label: "Team Name",
      width: "minmax(200px, 1.6fr)",
      render: (row) => <TeamNameCell name={row.name} description={row.description} />,
    },
    {
      key: "group",
      label: "Group",
      width: "minmax(100px, 0.55fr)",
      render: (row) => (
        <Typography
          sx={{
            color: "#405261",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            width: "100%",
          }}
        >
          {row.group}
        </Typography>
      ),
    },
    { key: "agents", label: "Agents", width: "minmax(72px, 0.4fr)" },
    { key: "totalChats", label: "Total Chats", width: "minmax(100px, 0.5fr)" },
    { key: "totalTickets", label: "Total Tickets", width: "minmax(108px, 0.55fr)" },
    {
      key: "status",
      label: "Status",
      width: "minmax(100px, 0.45fr)",
      render: (row) => <StatusChip status={row.status} />,
    },
    { key: "created", label: "Created", width: "minmax(108px, 0.55fr)" },
  ];

  const actionMenu = useMemo(
    () => [
      { key: "view", label: "View Detail" },
      {
        key: "archive",
        label: "Archive Team",
        hidden: (row) => !perms.teams.canArchive || row?.status === "archived",
      },
      {
        key: "activate",
        label: "Activate Team",
        hidden: (row) => !perms.teams.canArchive || row?.status !== "archived",
      },
      {
        key: "delete",
        label: "Delete Team",
        danger: true,
        hidden: () => !perms.teams.canDelete,
      },
    ],
    [perms]
  );

  const onAction = (type, row) => {
    if (type === "delete") {
      setDeleteTarget(row);
      setBulkDeleteCount(0);
    } else if (type === "archive") {
      setArchiveTarget(row);
      setBulkArchiveCount(0);
    } else if (type === "activate") {
      setActivateTarget(row);
    } else if (type === "view") {
      setDetailTeamId(null);
      navigate(`/people/teams/${row.id}`);
    }
  };

  const handleRowClick = (row) => {
    setDetailTeamId(row.id);
  };

  const handleCreateTeam = async (form) => {
    const body = buildTeamCreatePayloadFromForm(form);
    const result = await dispatch(createTeam(body));
    if (createTeam.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Team created"));
      setCreateModalOpen(false);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to create team"));
    }
  };

  const onBulkApply = async (values) => {
    const groupId = values.assignGroup;
    if (!selected.length || !groupId) return;

    const updates = await Promise.all(
      selected.map(async (teamId) => {
        const detailRes = await dispatch(fetchTeamById({ teamId }));
        const team = detailRes.payload?.data;
        if (!team) return detailRes;

        const body = buildTeamPayloadFromForm({
          name: team.name,
          description: team.description,
          groupIds: [groupId],
          members: (team.members || []).map(mapApiMemberToUi),
          channels: (team.channels || []).map(
            (c) => `${c.channel_type}:${c.channel_id}`
          ),
          channelObjects: team.channels || [],
        });
        return dispatch(updateTeam({ teamId, body }));
      })
    );

    const failed = updates.filter((r) => !updateTeam.fulfilled.match(r));
    if (failed.length) {
      toast.error("Some teams could not be updated");
    } else {
      toast.success("Changes applied");
      setSelected([]);
      reloadList();
    }
  };

  const onBulkArchive = () => {
    if (!selected.length) return;
    setArchiveTarget(null);
    setBulkArchiveCount(selected.length);
  };

  const onBulkDelete = () => {
    if (!selected.length) return;
    setDeleteTarget(null);
    setBulkDeleteCount(selected.length);
  };

  const handleArchiveConfirm = async () => {
    if (bulkArchiveCount > 0) {
      const result = await dispatch(
        bulkTeams({ action: "archive", team_ids: selected })
      );
      if (bulkTeams.fulfilled.match(result)) {
        toast.success(apiMessage(result.payload, `${bulkArchiveCount} team(s) archived`));
        setSelected([]);
        setBulkArchiveCount(0);
        reloadList();
      } else {
        toast.error(apiMessage(result.payload, "Failed to archive teams"));
      }
      return;
    }
    if (!archiveTarget) return;
    const result = await dispatch(archiveTeam({ teamId: archiveTarget.id }));
    if (archiveTeam.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${archiveTarget.name} archived`));
      setArchiveTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to archive team"));
    }
  };

  const handleDeleteConfirm = async () => {
    if (bulkDeleteCount > 0) {
      const results = await Promise.all(
        selected.map((id) => dispatch(deleteTeam({ teamId: id })))
      );
      const failed = results.filter((r) => !deleteTeam.fulfilled.match(r));
      if (failed.length) {
        toast.error("Some teams could not be deleted");
      } else {
        toast.success(`${bulkDeleteCount} team(s) deleted`);
        setSelected([]);
        setBulkDeleteCount(0);
        reloadList();
      }
      return;
    }
    if (!deleteTarget) return;
    const result = await dispatch(deleteTeam({ teamId: deleteTarget.id }));
    if (deleteTeam.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Team deleted"));
      setDeleteTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to delete team"));
    }
  };

  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    const result = await dispatch(activateTeam({ teamId: activateTarget.id }));
    if (activateTeam.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${activateTarget.name} activated`));
      setActivateTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to activate team"));
    }
  };

  const closeArchiveModal = () => {
    setArchiveTarget(null);
    setBulkArchiveCount(0);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setBulkDeleteCount(0);
  };

  const handleClearFilters = () => {
    syncGroupFilters([]);
  };

  const toolbarGroupOptions = useMemo(
    () => groupOptions.map((g) => ({ value: g.id, label: g.label })),
    [groupOptions]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <TeamToolbar
        searchQuery={search}
        onSearchChange={(val) => patchParams({ q: val || undefined })}
        groupFilters={groupFilters}
        onGroupFiltersChange={syncGroupFilters}
        onClearAll={handleClearFilters}
        groupOptions={toolbarGroupOptions}
        groupsLoading={formOptionsLoading}
      />

      <TeamBulkBar
        count={selected.length}
        onApply={onBulkApply}
        onReset={() => setSelected([])}
        onArchive={onBulkArchive}
        onDelete={onBulkDelete}
        showArchive={perms.teams.canArchive}
        showDelete={perms.teams.canDelete}
        showAssign={perms.teams.canBulkAssign}
        groups={groupOptions}
      />

      <Box
        className="Ticket-container-render"
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }}
      >
        {showListLoading ? (
          <PeopleTableSkeleton columns={columns} />
        ) : (
          <PeopleTable
            columns={columns}
            rows={filteredRows}
            selected={selected}
            setSelected={setSelected}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={(p) => setPage(p)}
            actionMenu={actionMenu}
            onAction={onAction}
            onRowClick={handleRowClick}
            highlightedRowId={detailTeamId}
            entityLabel="Teams"
            horizontalBleed={24}
          />
        )}
      </Box>

      {detailTeamId && (
        <TeamDetailSidebar
          team={detail?.id === detailTeamId ? detail : sidebarRawTeam}
          loading={detailLoading && detail?.id !== detailTeamId}
          onClose={() => setDetailTeamId(null)}
          onViewDetail={() => {
            setDetailTeamId(null);
            navigate(`/people/teams/${detailTeamId}`);
          }}
          onEdit={() => {
            setDetailTeamId(null);
            navigate(`/people/teams/${detailTeamId}/edit`);
          }}
          canEdit={perms.teams.canEdit}
        />
      )}

      <TeamCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onConfirm={handleCreateTeam}
        groupOptions={groupOptions}
        agentOptions={agentOptions}
        optionsLoading={formOptionsLoading}
        submitting={mutationLoading}
      />

      <TeamArchiveModal
        open={Boolean(archiveTarget || bulkArchiveCount > 0)}
        teamName={archiveTarget?.name}
        bulkCount={bulkArchiveCount}
        onClose={closeArchiveModal}
        onConfirm={handleArchiveConfirm}
      />

      <TeamDeleteModal
        open={Boolean(deleteTarget || bulkDeleteCount > 0)}
        teamName={deleteTarget?.name}
        bulkCount={bulkDeleteCount}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
      />

      <TeamActivateModal
        open={Boolean(activateTarget)}
        team={activateTarget}
        onClose={() => setActivateTarget(null)}
        onConfirm={handleActivateConfirm}
      />
    </Box>
  );
}
